import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromViewer from '@app/client/viewer/reducers/viewer.reducer';
import { DomainService } from '@app/core/services/domain.service';
import { LinkService } from '@app/core/services/link.service';
import { PromotionService } from '@app/core/services/promotion.service';
import { TransferService } from '@app/core/services/transfer.service';
import * as fromRoot from '@app/reducers';
import { generateRandomString } from '@app/shared/helpers/helpers';
import { urlIsFromMainDomain } from '@app/shared/helpers/location';
import { parsePreviewErrorResponse } from '@app/shared/helpers/parseErrorMessage';
import { addMetadataToFile } from '@app/shared/helpers/transferFile';
import { retryOn500 } from '@app/shared/operators/retryOn500';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { ViewerActions } from '../actions';
import { Transfer as TransferSdk } from '@smash-sdk-private/transfer/07-2022';
import { Link as LinkSdk } from '@smash-sdk-private/link/10-2019';
import { Region } from '@smash-sdk/core';
import { MacroTaskWrapperService } from '@app/core/services/MacroTaskWrapper.service';

@Injectable()
export class ViewerEffects {
    loadPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPreview),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getAccessTracking),
                this.store$.select(fromRoot.getDevice), // TO REMOVE
                this.store$.select(fromRoot.getSelectedCampaignFreemium), // TO REMOVE
            ),
            concatMap(([action, password, accessTracking, device, campaign]) => {
                const domain = this.domainService.detectDomainUrlWithoutProtocol();
                const domainWithoutQueryString = this.domainService.detectDomainUrlWithoutQueryString();
                return this.linkService.getTarget(encodeURIComponent(domainWithoutQueryString)).pipe(
                    retryOn500(),
                    concatMap(({ target }) => {
                        if (target?.url) {
                            let queryParams = domain.split('?')[1] ? '&' + decodeURIComponent(domain.split('?')[1]) : '';
                            if (accessTracking) {
                                queryParams += '&e=' + btoa(unescape(encodeURIComponent(accessTracking)));
                            }
                            return this.transferService.getPreviewByTarget(target.url, password, queryParams).pipe(
                                retryOn500(),
                                concatMap(({ transfer }) => {
                                    if (transfer?.status === Constant.transferStatus.Uploaded || transfer?.status === Constant.transferStatus.Processing) {
                                        // TO REMOVE
                                        if (urlIsFromMainDomain(transfer.transferUrl) && campaign) {
                                            const customization = device.isMobile ? campaign.customizationMobile : campaign.customization;
                                            return of(ViewerActions.LoadPreviewSuccess({ preview: { transfer: { ...transfer, customization } }, queryParams }));
                                        }
                                        // END TO REMOVE
                                        return of(ViewerActions.LoadPreviewSuccess({ preview: { transfer }, queryParams }));
                                    } else if (transfer?.status === Constant.transferStatus.Suspended) {
                                        this.router.navigateByUrl('/suspended');
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Expired) {
                                        this.router.navigateByUrl('/expired');
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Deleted || transfer?.status === Constant.transferStatus.Deleting) {
                                        this.router.navigateByUrl('/deleted');
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Uploading) {
                                        this.router.navigateByUrl('/404');
                                        return of({ type: 'noop' });
                                    }
                                }),
                                catchError((error: HttpErrorResponse) => {
                                    const errorMessage = error.message;
                                    if (error.status === 403) {
                                        const errorType = parsePreviewErrorResponse(error);
                                        if ((action.password && errorType === Constant.errorTypes.Password) || (action.accessTracking && errorType === Constant.errorTypes.Email)) {
                                            return [
                                                ViewerActions.LockPreview({ errorType }),
                                                ViewerActions.UnlockPreviewFailure({ errorType }),
                                            ];
                                        } else if (errorType === Constant.errorTypes.Password || errorType === Constant.errorTypes.Email) {
                                            // need to differentiate error errorType password or email ?
                                            return [
                                                ViewerActions.LockPreview({ errorType }),
                                            ];
                                        } else if (errorType === Constant.errorTypes.OwnerUsageExceeded || errorType === Constant.errorTypes.AccessorUsageExceeded) {
                                            this.router.navigateByUrl('/quota-exceeded');
                                            return of(ViewerActions.LoadPreviewFailure({ error: errorType }));
                                        } else {
                                            return of(ViewerActions.LoadPreviewFailure({ error: errorType }));
                                        }
                                    } else if (error.status === 404) {
                                        this.router.navigateByUrl('/404');
                                        return of(ViewerActions.LoadPreviewFailure({ error: errorMessage }));
                                    } else {
                                        throw error;
                                    }
                                })
                            );
                        }

                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 404) {
                            this.router.navigateByUrl('/404');
                        }
                        return of(ViewerActions.LoadPreviewFailure({ error: error.message }));
                    })
                );
            })
        )
    );

    loadPreviewSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPreviewSuccess),
            map(action => action.preview),
            withLatestFrom(
                this.store$.select(fromRoot.getSelectedLanguage),
                this.store$.select(fromRoot.getSelectedCampaignFreemium), // TO REMOVE
            ),
            concatMap(([preview, language, campaign]) => {
                // TO REMOVE
                if (urlIsFromMainDomain(preview.transfer.transferUrl) && campaign) {
                    const filteredPromotions = campaign.promotions.filter(p => p.target === 'External' || p.target === 'All');
                    const filteredPromotionsOnLanguage = filteredPromotions.filter(promotion => promotion.language === language);
                    if (filteredPromotionsOnLanguage.length) {
                        return [
                            ViewerActions.LoadPromotionSuccess({ promotion: filteredPromotionsOnLanguage[0] }),
                            ViewerActions.LoadFiles()
                        ];
                    } else {
                        return [
                            ViewerActions.LoadPromotionSuccess({ promotion: filteredPromotions[0] }),
                            ViewerActions.LoadFiles()
                        ];
                    }
                }
                // END TO REMOVE
                if (preview?.transfer?.promotion?.url) {
                    return [
                        ViewerActions.LoadPromotion({ promotionUrl: preview.transfer.promotion.url }),
                        ViewerActions.LoadFiles()
                    ];
                } else {
                    return [
                        ViewerActions.LoadFiles()
                    ];
                }
            })
        )
    );

    loadPromotion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPromotion),
            map(action => action.promotionUrl),
            concatMap((promotionUrl) =>
                this.promotionService.loadPromotionByUrl(promotionUrl).pipe(
                    retryOn500(),
                    map(({ promotion }) => ViewerActions.LoadPromotionSuccess({ promotion })),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadPromotionFailure({ error: error.message }))),
                )
            )
        )
    );

    loadFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getQueryParams),
                this.store$.select(fromRoot.getDevice),
            ),
            concatMap(([action, password, preview, queryParams, device]) => {
                const limit = (device.isTablet ? 10 : 9);
                return this.transferService.listTransferFilesPreview(preview.transfer.id, limit, queryParams, password).pipe(
                    retryOn500(),
                    map(({ files: newFiles, next, urlsExpiredAt }) => {
                        const batch = { id: generateRandomString(), next };
                        const filesWithMetadata = newFiles.map((file, index) => ({ ...addMetadataToFile(file), expiry: urlsExpiredAt, index, batch }));
                        return ViewerActions.LoadFilesSuccess({ files: filesWithMetadata, next });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadFilesFailure({ error: error.message })))
                );
            })
        )
    );

    loadMoreFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadMoreFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getQueryParams),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getNextFile),
                this.store$.select(fromRoot.getDevice),
            ),
            concatMap(([action, password, preview, queryParams, files, next, device]) => {
                const limit = (device.isTablet ? 10 : 9);
                const filesAlreadyRetrievedLength = files.length;
                return this.transferService.getMoreFiles(preview.transfer.id, next, limit, queryParams, password).pipe(
                    retryOn500(),
                    map(({ files: newFiles, next: newNext, urlsExpiredAt }) => {
                        const batch = { id: generateRandomString(), next };
                        const filesWithMetadata = newFiles.map((file, index) => ({ ...addMetadataToFile(file), expiry: urlsExpiredAt, index: index + filesAlreadyRetrievedLength, batch }));
                        return ViewerActions.LoadMoreFilesSuccess({ files: filesWithMetadata, next: newNext });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadMoreFilesFailure({ error: error.message })))
                )
            })
        )
    );

    refreshFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.RefreshFileAndAssociatedFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getQueryParams),
            ),
            concatMap(([action, password, preview, files, queryParams]) => {
                const fileToRefresh = action.file;
                const associatedFiles = files.filter(file => file.batch.id === fileToRefresh.batch.id);
                const associatedFilesLength = associatedFiles.length;
                return this.transferService.getMoreFiles(preview.transfer.id, fileToRefresh.batch.next, associatedFilesLength, queryParams, password).pipe(
                    map(({ files: newFilesRetrieved, urlsExpiredAt }) => {
                        const newFilesToReturn = newFilesRetrieved.map(newFileRetrieved => {
                            const oldFile = files.find(oldFile => newFileRetrieved.index === oldFile.index);
                            const newFilesWithMetadata = { ...oldFile, ...newFileRetrieved, expiry: urlsExpiredAt };
                            return newFilesWithMetadata;
                        });
                        return ViewerActions.RefreshFileAndAssociatedFilesSuccess({ files: newFilesToReturn });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.RefreshFileAndAssociatedFilesFailure({ error: error.message })))
                )
            })
        )
    );

    requestDownloadUrls$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.RequestDownloadUrls),
            withLatestFrom(
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getSelectedFiles),
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getQueryParams),
            ),
            concatMap(([action, preview, files, selectedFiles, password, queryParams]) => {
                // In case we just need to download one precise file (modal preview)
                if (action.file) {
                    const body = { files: [{ id: action.file.id }] };
                    return this.transferService.putBatchUrls(preview.transfer.id, queryParams, body, password).pipe(
                        map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                        catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                    );
                } else {
                    const transferFilesNumber = preview?.transfer?.filesNumber;
                    if (transferFilesNumber === 1) {
                        const body = { files: files.map(file => ({ id: file.id })) };
                        return this.transferService.putBatchUrls(preview.transfer.id, queryParams, body, password).pipe(
                            map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                            catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                        );
                    } else if (transferFilesNumber > 1) {
                        if (selectedFiles?.length > 0) {
                            const body = { files: selectedFiles.map(file => ({ id: file.id })) };
                            return this.transferService.putBatchUrls(preview.transfer.id, queryParams, body, password).pipe(
                                map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                                catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                            );
                        } else {
                            return this.transferService.putBatchUrls(preview.transfer.id, queryParams, { files: [] }, password).pipe(
                                map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: [transfer.url] })),
                                catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                            );
                        }
                    }
                }
            })
        )
    );

    loadTransferMetas$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadTransferMetas),
            concatMap(() => {
                const linkSdk = new LinkSdk();
                const targetId = this.domainService.detectDomainUrlWithoutQueryString();
                // https://dev.to/krisplatis/angular-ssr-v16-saying-goodbye-to-a-sneaky-trick-macrotask-wrapping-for-http-calls-487b
                return this.macroTaskWrapper.wrapMacroTask("linkSdk.getTarget", linkSdk.getTarget({ targetId }))
                    .then(({ target }) => ViewerActions.LoadTransferInternal({ transferId: target.target, region: target.region }))
                    // TO FIX ? if request fail it wont be catched here.. it will be silent
                    .catch((error) => ViewerActions.LoadTransferMetasFailure({ error: error.message }));
            })
        )
    );

    loadTransferInternal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadTransferInternal),
            concatMap(({ transferId, region }) => {
                const transferSdk = new TransferSdk({ region: region as Region });
                return this.macroTaskWrapper.wrapMacroTask("transferSdk.getTransferInternal", transferSdk.getTransferInternal({ transferId }))
                    .then(({ transfer }) => ViewerActions.LoadTransferMetasSuccess({ transfer: { ...transfer, targetPath: this.domainService.detectPath().slice(1) } }))
                    .catch((error) => ViewerActions.LoadTransferMetasFailure({ error: error.message }));
            })
        )
    );

    constructor(
        private macroTaskWrapper: MacroTaskWrapperService,
        private actions$: Actions,
        private readonly store$: Store<fromRoot.State & fromViewer.State>,
        private transferService: TransferService,
        private linkService: LinkService,
        private domainService: DomainService,
        private promotionService: PromotionService,
        // private state: TransferState,
        private router: Router,
    ) { }
}
